/**
 * Generates a UUID v4 used as `client_lead_id` — the join key between the
 * GA4 event (dataLayer) and the n8n/warehouse row. Exactly ONE UUID per
 * form session; the same value is attached to the form POST AND the
 * dataLayer submit event.
 *
 * Prefers crypto.randomUUID() (secure context, modern browsers), falls back
 * to RFC 4122 v4 via crypto.getRandomValues, last resort Math.random (old
 * WebViews / http embeds). No npm uuid dependency.
 *
 * v4 is recognizable by `-4xxx-` in group 3 and `[89ab]xxx` in group 4 —
 * cleanly distinguishable from the server-side SSC lead_id (UUID v1).
 *
 * NOT persisted in cookies/storage and NOT sent to ad platforms —
 * purely additive field, internal join key.
 */
export function generateClientLeadId() {
    try {
        if (typeof crypto !== 'undefined') {
            if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
            if (typeof crypto.getRandomValues === 'function') {
                const bytes = crypto.getRandomValues(new Uint8Array(16));
                bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
                bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 10xx
                const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
                return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
            }
        }
    } catch (e) {
        /* fail-safe: continue to Math.random fallback */
    }
    // Last resort: not cryptographic, but valid v4 format
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}
